import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, SignedIn, SignedOut } from '@clerk/clerk-react';
import { api } from '../utils/api';

/**
 * Auth callback after Clerk sign-in/sign-up. Calls complete-signup and redirects.
 * Query: type=owner|customer, businessId= (optional, for customer)
 */
export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken, isLoaded } = useAuth();
  const [error, setError] = useState(null);

  const type = searchParams.get('type')?.toLowerCase();
  const businessIdParam = searchParams.get('businessId');
  const businessId = businessIdParam ? parseInt(businessIdParam, 10) : null;

  useEffect(() => {
    if (!isLoaded || !type) return;

    const run = async () => {
      const token = await getToken();
      if (!token) {
        setError('Please sign in first.');
        return;
      }
      if (type !== 'owner' && type !== 'customer') {
        setError('Invalid type. Use owner or customer.');
        return;
      }

      try {
        const body = { userType: type };
        if (type === 'customer' && businessId) body.businessId = businessId;

        const res = await api('/api/users/me/complete-signup', {
          getToken,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = data.error || data.detail || `Error ${res.status}`;
          setError(msg);
          return;
        }

        // Only set customer context when user is actually a customer. If an owner signed in from the
        // menu (type=customer), we must NOT set it—otherwise OwnerGuard would block owner routes.
        const actualUserType = data.userType?.toLowerCase();
        if (type === 'customer' && actualUserType === 'customer') {
          sessionStorage.setItem('gng_customerContext', '1');
        } else {
          sessionStorage.removeItem('gng_customerContext');
        }

        const redirectTo = data.redirectTo || '/';
        const ref = searchParams.get('ref');
        // Belt-and-suspenders: record ref visit from auth callback when we have ref
        // (catches cases where user signed in before Menu loaded and record-ref-visit never ran)
        let bid = businessId;
        if (ref && !bid && redirectTo.startsWith('/m/')) {
          const m = redirectTo.match(/^\/m\/([^/?]+)/);
          if (m) {
            try {
              const bizRes = await api(`/api/businesses/by-slug/${encodeURIComponent(m[1])}`, { getToken });
              if (bizRes.ok) {
                const biz = await bizRes.json();
                bid = biz?.id ?? null;
              }
            } catch (_) { /* ignore */ }
          }
        }
        if (ref && bid) {
          try {
            await api(`/api/businesses/${bid}/record-ref-visit?ref=${encodeURIComponent(ref)}`, {
              getToken,
              method: 'POST',
            });
          } catch (_) { /* best-effort, don't block redirect */ }
        }
        const finalUrl = ref && redirectTo.startsWith('/m/')
          ? `${redirectTo}${redirectTo.includes('?') ? '&' : '?'}ref=${encodeURIComponent(ref)}`
          : redirectTo;
        navigate(finalUrl, { replace: true });
      } catch (err) {
        setError(err.message || 'Something went wrong');
      }
    };

    run();
  }, [isLoaded, type, businessId, getToken, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh]">
      <SignedIn>
        {error ? (
          <div className="rounded-lg bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 max-w-md">
            {error}
          </div>
        ) : (
          <p className="text-slate-600 dark:text-slate-400">Completing signup…</p>
        )}
      </SignedIn>
      <SignedOut>
        <p className="text-slate-600 dark:text-slate-400">Please sign in to continue.</p>
      </SignedOut>
    </div>
  );
}
